import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * bn-empty-state is the centered placeholder for empty lists, no-results, and
 * not-found. It shows an optional glyph (an `icon` string — an emoji or a single
 * char), a `heading`, a `message`, and an `actions` slot for a call to action.
 *
 *   <bn-empty-state icon="◲" heading="No workspaces yet" message="Create one to get started.">
 *     <bn-button slot="actions" variant="primary">New workspace</bn-button>
 *   </bn-empty-state>
 */
@customElement("bn-empty-state")
export class BnEmptyState extends LitElement {
  @property() icon = "";
  @property() heading = "";
  @property() message = "";

  static override styles = css`
    :host {
      display: block;
    }
    .box {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: var(--bino-space-sm);
      padding: var(--bino-space-xl) var(--bino-space-lg);
      color: var(--bino-text-secondary);
      font-family: var(--bino-font-sans);
    }
    .icon {
      font-size: 2rem;
      line-height: 1;
      color: var(--bino-text-secondary);
      opacity: 0.8;
    }
    .heading {
      font-size: var(--bino-font-size-md);
      font-weight: 600;
      color: var(--bino-text);
    }
    .message {
      font-size: var(--bino-font-size-base);
      max-width: 32rem;
    }
    .actions {
      margin-top: var(--bino-space-sm);
    }
  `;

  override render() {
    return html`
      <div class="box">
        ${this.icon ? html`<div class="icon" aria-hidden="true">${this.icon}</div>` : ""}
        ${this.heading ? html`<div class="heading">${this.heading}</div>` : ""}
        ${this.message ? html`<div class="message">${this.message}</div>` : ""}
        <div class="actions"><slot name="actions"></slot></div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-empty-state": BnEmptyState;
  }
}
